import { format, parseISO, formatDistanceToNow, isToday, isYesterday, differenceInDays } from 'date-fns'

/**
 * Format a date as 'MMM d, yyyy' (e.g., Oct 13, 2024).
 * @param {string} dateString - ISO date string.
 * @returns {string} Formatted date.
 */
export function formatDate(dateString) {
  const date = parseISO(dateString)
  return format(date, 'MMM d, yyyy') // Example: Oct 13, 2024
}

/**
 * Format a date as a short relative time string (e.g., '15d', '3h', '45m').
 * @param {string} dateString - ISO date string.
 * @returns {string} Short relative time string.
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return ''
  try {
    const date = parseISO(dateString)
    if (isNaN(date.getTime())) return ''

    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffWeeks = Math.floor(diffDays / 7)
    const diffMonths = Math.floor(diffDays / 30)

    if (diffMins < 1) return 'now'
    if (diffMins < 60) return `${diffMins}m`
    if (diffHours < 24) return `${diffHours}h`
    if (diffDays < 7) return `${diffDays}d`
    if (diffWeeks < 4) return `${diffWeeks}w`
    if (diffMonths < 12) return `${diffMonths}mo`

    // For very old dates, fall back to formatted date
    return format(date, 'MMM d, yyyy')
  } catch (e) {
    return ''
  }
}

/**
 * Format a review date as a readable relative/absolute string
 * (e.g., 'Today', 'Yesterday', '3 days ago', 'Jan 15').
 * @param {string} dateString - ISO date string.
 * @returns {string} Human-readable date label.
 */
export function formatReviewedDate(dateString) {
  if (!dateString) return ''
  try {
    const date = parseISO(dateString)
    if (isNaN(date.getTime())) return ''

    if (isToday(date)) return 'Today'
    if (isYesterday(date)) return 'Yesterday'

    const days = differenceInDays(new Date(), date)
    if (days < 7) return `${days} days ago`
    if (days < 30) {
      const weeks = Math.floor(days / 7)
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`
    }

    const now = new Date()
    if (date.getFullYear() === now.getFullYear()) {
      return format(date, 'MMM d')
    }
    return format(date, 'MMM d, yyyy')
  } catch (e) {
    return ''
  }
}
